<template>
  <div>
    <v-toolbar
      dense
      tabs
      color="primary darken-2"
      dark
      class="page_title"
    >
      <v-toolbar-title class="pa-0 pr-0 mt-1">
        <v-layout row>
          <v-flex xs10 sm11 text-xs-left>
            <a href="javascript: history.go(-1)" class="pa-0">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
            Investment history
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
              <router-link :to="'/savings'" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-tabs
        v-model="tabs"
        color="transparent"
        slider-color="white"
        slot="extension"
        grow
      >
        <v-tab
          v-for="tabsItem in tabsItems"
          :key="tabsItem.id"
          :to="tabsItem.link"
        >
          {{ tabsItem.title }}
        </v-tab>
      </v-tabs>
    </v-toolbar>

  <div class="payments-history-container">
    <!-- Header -->
    <v-card class="elevation-4 mb-4" style="border-radius: 12px;">
      <v-card-title class="teal darken-2 white--text">
        <v-icon color="white" class="mr-2">fas fa-history</v-icon>
        Payment History - Investment #{{ investmentId }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$router.go(-1)">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Summary Stats -->
    <v-layout row class="mb-4">
      <v-flex xs6 sm3 class="px-1">
        <v-card class="stat-card green lighten-5" elevation="2">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="green darken-2">fas fa-check-circle</v-icon>
            <div class="caption mt-1">Validated</div>
            <div class="subtitle-1 font-weight-bold green--text">
              {{ validatedCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs6 sm3 class="px-1">
        <v-card class="stat-card orange lighten-5" elevation="2">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="orange darken-2">fas fa-clock</v-icon>
            <div class="caption mt-1">Pending</div>
            <div class="subtitle-1 font-weight-bold orange--text">
              {{ pendingCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs6 sm3 class="px-1">
        <v-card class="stat-card red lighten-5" elevation="2">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="red darken-2">fas fa-times-circle</v-icon>
            <div class="caption mt-1">Cancelled</div>
            <div class="subtitle-1 font-weight-bold red--text">
              {{ cancelledCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs6 sm3 class="px-1">
        <v-card class="stat-card blue lighten-5" elevation="2">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="blue darken-2">fas fa-money-bill-wave</v-icon>
            <div class="caption mt-1">Total Validated</div>
            <div class="subtitle-1 font-weight-bold blue--text">
              {{ totalValidated | formatAmount }} FCFA
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Filter Card -->
    <v-card class="elevation-2 mb-4" style="border-radius: 12px;">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="4">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Filter by Status"
              outlined
              dense
              prepend-icon="fas fa-filter"
              color="teal"
            ></v-select>
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              type="date"
              :max="endDate || today"
              outlined
              dense
              prepend-icon="fas fa-calendar-alt"
              color="teal"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="3">
            <v-text-field
              v-model="endDate"
              label="End Date"
              type="date"
              :min="startDate"
              :max="today"
              outlined
              dense
              prepend-icon="fas fa-calendar-check"
              color="teal"
            ></v-text-field>
          </v-col>

          <v-col cols="12" sm="2" class="text-center">
            <v-btn
              color="teal darken-2"
              fab
              dark
              @click="applyFilters"
            >
              <v-icon>fas fa-search</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Payments List -->
    <v-card class="elevation-2" style="border-radius: 12px;">
      <v-card-title class="teal--text darken-2">
        <v-icon left color="teal darken-2">fas fa-list</v-icon>
        {{ filteredPayments.length }} payment(s) found
      </v-card-title>

      <div style="max-height: 600px; overflow: auto">
        <v-card
          v-for="payment in filteredPayments"
          :key="payment.id"
          class="payment-card mb-2 mx-3"
          :class="getPaymentCardClass(payment.validation_status)"
        >
          <v-card-text class="pa-3">
            <v-layout row wrap align-center>
              <!-- Icon & Status -->
              <v-flex xs12 sm2 class="text-center">
                <v-avatar :color="getStatusColor(payment.validation_status)" size="48">
                  <v-icon dark>{{ getStatusIcon(payment.validation_status) }}</v-icon>
                </v-avatar>
                <div class="caption mt-1" :class="getStatusTextClass(payment.validation_status)">
                  {{ getStatusLabel(payment.validation_status) }}
                </div>
              </v-flex>

              <!-- Payment Details -->
              <v-flex xs12 sm5>
                <div class="payment-details">
                  <h3 class="teal--text darken-2 mb-1">
                    {{ payment.description }}
                  </h3>

                  <div class="caption grey--text">
                    <v-icon x-small>fas fa-calendar</v-icon>
                    {{ payment.transaction_date | formatDateTime }}
                  </div>

                  <div class="caption mt-1" v-if="payment.phone_number">
                    <v-icon x-small>fas fa-phone</v-icon>
                    {{ payment.operator }} - {{ payment.phone_number }}
                  </div>

                  <div class="caption mt-1" v-if="payment.reference">
                    <v-icon x-small>fas fa-hashtag</v-icon>
                    Ref: {{ payment.reference }}
                  </div>
                </div>
              </v-flex>

              <!-- Amount & Month Info -->
              <v-flex xs12 sm3 class="text-center">
                <div class="amount-box" :class="getAmountBoxClass(payment.validation_status)">
                  <div class="caption grey--text">Amount</div>
                  <h2 class="font-weight-bold mb-0">
                    {{ payment.amount | formatAmount }} FCFA
                  </h2>
                </div>

                <v-chip
                  v-if="payment.month_number"
                  small
                  color="purple"
                  dark
                  class="mt-2"
                >
                  <v-icon x-small left>fas fa-calendar-alt</v-icon>
                  Month {{ payment.month_number }}
                </v-chip>
              </v-flex>

              <!-- Actions -->
              <v-flex xs12 sm2 class="text-center">
                <v-chip
                  small
                  :color="getStatusColor(payment.validation_status)"
                  dark
                  @click="showDetails(payment)"
                  style="cursor: pointer;"
                >
                  <v-icon x-small left>fas fa-eye</v-icon>
                  Details
                </v-chip>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <!-- No Data -->
        <div v-if="filteredPayments.length === 0" class="pa-5 text-center">
          <v-icon size="64" color="grey lighten-1">fas fa-inbox</v-icon>
          <p class="grey--text mt-3 title">No payments found</p>
          <p class="grey--text caption">Try adjusting your filters</p>
        </div>
      </div>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="500">
      <v-card v-if="selectedPayment">
        <v-card-title :class="getStatusColor(selectedPayment.validation_status) + ' white--text'">
          <v-icon color="white" left>fas fa-info-circle</v-icon>
          Payment Details
        </v-card-title>

        <v-card-text class="pt-4">
          <v-list dense>
            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-sub-title>Status</v-list-tile-sub-title>
                <v-list-tile-title>
                  <v-chip small :color="getStatusColor(selectedPayment.validation_status)" dark>
                    {{ getStatusLabel(selectedPayment.validation_status) }}
                  </v-chip>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-divider class="my-2"></v-divider>

            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-sub-title>Amount</v-list-tile-sub-title>
                <v-list-tile-title class="title">
                  {{ selectedPayment.amount | formatAmount }} FCFA
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-sub-title>Date & Time</v-list-tile-sub-title>
                <v-list-tile-title>
                  {{ selectedPayment.transaction_date | formatDateTime }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile v-if="selectedPayment.month_number">
              <v-list-tile-content>
                <v-list-tile-sub-title>Month</v-list-tile-sub-title>
                <v-list-tile-title>
                  Month {{ selectedPayment.month_number }} / 10
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-divider class="my-2"></v-divider>

            <v-list-tile v-if="selectedPayment.phone_number">
              <v-list-tile-content>
                <v-list-tile-sub-title>Payment Method</v-list-tile-sub-title>
                <v-list-tile-title>
                  {{ selectedPayment.operator }} - {{ selectedPayment.phone_number }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile v-if="selectedPayment.reference">
              <v-list-tile-content>
                <v-list-tile-sub-title>Reference</v-list-tile-sub-title>
                <v-list-tile-title>
                  {{ selectedPayment.reference }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>

            <v-divider class="my-2"></v-divider>

            <v-list-tile>
              <v-list-tile-content>
                <v-list-tile-sub-title>Description</v-list-tile-sub-title>
                <v-list-tile-title class="body-2">
                  {{ selectedPayment.description }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      investmentId: null,
      payments: [],
      statusFilter: 'All',
      statusOptions: ['All', 'Validated', 'Pending', 'Cancelled'],
      startDate: null,
      endDate: null,
      today: new Date().toISOString().substr(0, 10),
      detailsDialog: false,
      selectedPayment: null
    }
  },

  computed: {
    filteredPayments() {
      let filtered = this.payments

      // Filter by status
      if (this.statusFilter !== 'All') {
        filtered = filtered.filter(p => p.validation_status === this.statusFilter)
      }

      // Filter by date range
      if (this.startDate) {
        filtered = filtered.filter(p => p.transaction_date >= this.startDate)
      }
      if (this.endDate) {
        const endDateTime = this.endDate + 'T23:59:59'
        filtered = filtered.filter(p => p.transaction_date <= endDateTime)
      }

      return filtered
    },

    validatedCount() {
      return this.payments.filter(p => p.validation_status === 'Validated').length
    },

    pendingCount() {
      return this.payments.filter(p => p.validation_status === 'Pending').length
    },

    cancelledCount() {
      return this.payments.filter(p => p.validation_status === 'Cancelled').length
    },

    totalValidated() {
      return this.payments
        .filter(p => p.validation_status === 'Validated')
        .reduce((sum, p) => sum + parseFloat(p.amount), 0)
    }
  },

  filters: {
    formatAmount(value) {
      return new Intl.NumberFormat('fr-FR').format(value)
    },

    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },

  mounted() {
    this.investmentId = this.$route.params.investment_id
    this.loadPayments()
    document.title = `Investment #${this.investmentId} - Payment History`
  },

  methods: {
    async loadPayments() {
      this.$store.commit('setIsLoading', true)

      try {
        const response = await axios.get(
          `/api/v1/savings/investments/${this.investmentId}/payments-history/`
        )
        this.payments = response.data.payments || []
      } catch (error) {
        this.$store.commit('setSnackBarMessage', 'Error loading payment history')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },

    applyFilters() {
      // Filters are computed automatically
    },

    showDetails(payment) {
      this.selectedPayment = payment
      this.detailsDialog = true
    },

    getStatusColor(status) {
      const colors = {
        'Validated': 'green',
        'Pending': 'orange',
        'Cancelled': 'red'
      }
      return colors[status] || 'grey'
    },

    getStatusIcon(status) {
      const icons = {
        'Validated': 'fas fa-check-circle',
        'Pending': 'fas fa-clock',
        'Cancelled': 'fas fa-times-circle'
      }
      return icons[status] || 'fas fa-question-circle'
    },

    getStatusLabel(status) {
      return status || 'Unknown'
    },

    getStatusTextClass(status) {
      const classes = {
        'Validated': 'green--text font-weight-bold',
        'Pending': 'orange--text font-weight-bold',
        'Cancelled': 'red--text font-weight-bold'
      }
      return classes[status] || 'grey--text'
    },

    getPaymentCardClass(status) {
      const classes = {
        'Validated': 'validated-card',
        'Pending': 'pending-card',
        'Cancelled': 'cancelled-card'
      }
      return classes[status] || ''
    },

    getAmountBoxClass(status) {
      const classes = {
        'Validated': 'amount-validated',
        'Pending': 'amount-pending',
        'Cancelled': 'amount-cancelled'
      }
      return classes[status] || ''
    }
  }
}
</script>

<style scoped>
.payments-history-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.stat-card {
  border-radius: 12px !important;
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
}

.payment-card {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.payment-card:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.validated-card {
  border-left-color: #4caf50 !important;
  background: #f1f8f4 !important;
}

.pending-card {
  border-left-color: #ff9800 !important;
  background: #fff8f1 !important;
}

.cancelled-card {
  border-left-color: #f44336 !important;
  background: #ffebee !important;
}

.amount-box {
  padding: 12px;
  border-radius: 8px;
  background: white;
  border: 2px solid #e0e0e0;
}

.amount-validated {
  border-color: #4caf50;
  background: #e8f5e9;
}

.amount-pending {
  border-color: #ff9800;
  background: #fff3e0;
}

.amount-cancelled {
  border-color: #f44336;
  background: #ffebee;
}

.payment-details {
  line-height: 1.6;
}
</style>